import { z } from 'zod';

const uuid = z.string().uuid();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');

/**
 * Every automatic event the notifications pipeline delivers (ticket #108, scoping 3.9). The
 * catalog in `docs/features/notifications.md` gives each one its recipients, text and deep link.
 */
export const NOTIFICATION_TYPES = [
  'attendance.absence',
  'attendance.threshold',
  'grade.published',
  'report_card.available',
  'assignment.published',
  'assignment.reviewed',
  'charge.due',
  'charge.overdue',
  'announcement.published',
] as const;

/**
 * One of the catalog's event types.
 */
export const notificationTypeSchema = z.enum(NOTIFICATION_TYPES);

/**
 * The channels a person turns on or off per event type; the inbox row is always written.
 */
export const NOTIFICATION_CHANNELS = ['push', 'email'] as const;

/**
 * Payload of `attendance.absence`: a mark that just became an absence (#105).
 */
export const absencePayloadSchema = z.object({
  studentId: uuid,
  date: isoDate,
  recordId: uuid.optional(),
  groupId: uuid.optional(),
  groupName: z.string().optional(),
  subjectId: uuid.nullable().optional(),
  blockId: uuid.nullable().optional(),
});

/**
 * Payload of `attendance.threshold`: a student reached the school's absence threshold in a
 * period (#105).
 */
export const thresholdPayloadSchema = z.object({
  studentId: uuid,
  periodId: uuid,
  periodName: z.string().optional(),
  groupId: uuid.optional(),
  absencePercent: z.number(),
  thresholdPercent: z.number(),
  marks: z.number().int().optional(),
  absences: z.number().int().optional(),
});

/**
 * Payload of `grade.published`: grades of a period became visible to families (#106), for one
 * student (the event's `studentId`) or for a whole group (`groupId`).
 */
export const gradePublishedPayloadSchema = z.object({
  periodId: uuid,
  periodName: z.string().optional(),
  subjectId: uuid.nullable().optional(),
  groupId: uuid.optional(),
});

/**
 * Payload of `report_card.available`: a period's report card is ready (#107), for one student
 * or a whole group.
 */
export const reportCardPayloadSchema = z.object({
  periodId: uuid,
  periodName: z.string().optional(),
  groupId: uuid.optional(),
});

/**
 * Payload of `assignment.published`: a teacher published a task for a group (#149).
 */
export const assignmentPublishedPayloadSchema = z.object({
  assignmentId: uuid,
  groupId: uuid,
  subjectId: uuid.nullable().optional(),
  title: z.string().min(1),
  dueOn: isoDate.nullable().optional(),
});

/**
 * Payload of `assignment.reviewed`: a teacher reviewed a student's submission (#149).
 */
export const assignmentReviewedPayloadSchema = z.object({
  assignmentId: uuid,
  submissionId: uuid.optional(),
  title: z.string().min(1),
  outcome: z.enum(['accepted', 'redo']),
});

/**
 * Payload of `charge.due` and `charge.overdue`: a family charge with an outstanding balance
 * (billing, #59). `amount` is the balance still owed, as a decimal string.
 */
export const chargePayloadSchema = z.object({
  chargeId: uuid,
  description: z.string().min(1),
  dueOn: isoDate,
  amount: z.string().regex(/^\d+(\.\d{1,2})?$/),
  currency: z.string().length(3),
});

/**
 * Payload of `announcement.published` (#66): the recipients are the announcement's own.
 */
export const announcementPayloadSchema = z.object({
  announcementId: uuid,
  title: z.string().min(1),
  body: z.string(),
});

/**
 * Query of `GET /me/inbox`: newest first, optionally one child's history or only unread rows.
 */
export const inboxQuerySchema = z.object({
  studentId: uuid.optional(),
  unread: z
    .enum(['true', 'false'])
    .transform((value) => value === 'true')
    .optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(50).default(20),
});

/**
 * An Expo push token as the app gets it from `getExpoPushTokenAsync`.
 */
export const expoPushTokenSchema = z
  .string()
  .regex(/^Expo(nent)?PushToken\[[A-Za-z0-9_-]{1,200}\]$/, 'Not an Expo push token');

/**
 * Body of `PUT /me/devices`: registers (or refreshes) this device for push.
 */
export const registerDeviceInputSchema = z.object({
  token: expoPushTokenSchema,
  platform: z.enum(['ios', 'android', 'web']),
});

/**
 * Body of `PUT /me/notification-preferences`: the channels to change per event type; a channel
 * left out keeps its value.
 */
export const updateNotificationPreferencesInputSchema = z.object({
  items: z
    .array(
      z.object({
        type: notificationTypeSchema,
        push: z.boolean().optional(),
        email: z.boolean().optional(),
      }),
    )
    .min(1)
    .max(NOTIFICATION_TYPES.length),
});

/**
 * An event type of the catalog.
 */
export type NotificationType = z.infer<typeof notificationTypeSchema>;
/**
 * A delivery channel a person can turn off.
 */
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];
/**
 * Payload of `attendance.absence`.
 */
export type AbsencePayload = z.infer<typeof absencePayloadSchema>;
/**
 * Payload of `attendance.threshold`.
 */
export type ThresholdPayload = z.infer<typeof thresholdPayloadSchema>;
/**
 * Payload of `grade.published`.
 */
export type GradePublishedPayload = z.infer<typeof gradePublishedPayloadSchema>;
/**
 * Payload of `report_card.available`.
 */
export type ReportCardPayload = z.infer<typeof reportCardPayloadSchema>;
/**
 * Payload of `assignment.published`.
 */
export type AssignmentPublishedPayload = z.infer<typeof assignmentPublishedPayloadSchema>;
/**
 * Payload of `assignment.reviewed`.
 */
export type AssignmentReviewedPayload = z.infer<typeof assignmentReviewedPayloadSchema>;
/**
 * Payload of `charge.due` and `charge.overdue`.
 */
export type ChargePayload = z.infer<typeof chargePayloadSchema>;
/**
 * Payload of `announcement.published`.
 */
export type AnnouncementPayload = z.infer<typeof announcementPayloadSchema>;
/**
 * Query of `GET /me/inbox`.
 */
export type InboxQuery = z.infer<typeof inboxQuerySchema>;
/**
 * Body of `PUT /me/devices`.
 */
export type RegisterDeviceInput = z.infer<typeof registerDeviceInputSchema>;
/**
 * Body of `PUT /me/notification-preferences`.
 */
export type UpdateNotificationPreferencesInput = z.infer<
  typeof updateNotificationPreferencesInputSchema
>;

/**
 * One row of a person's inbox: what happened, about which child, whether they read it and the
 * app screen it opens.
 */
export type InboxItem = {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  /**
   * The app path it opens (for example `/students/{id}/attendance`); null when it has none.
   */
  link: string | null;
  studentId: string | null;
  studentName: string | null;
  /**
   * The event's payload (ids the app may need), as the catalog defines it.
   */
  data: Record<string, unknown>;
  readAt: string | null;
  createdAt: string;
};

/**
 * One child in a guardian's inbox (a student sees themselves), with their unread count.
 */
export type InboxChild = {
  studentId: string;
  name: string;
  unread: number;
};

/**
 * `GET /me/inbox`: a page of rows, the unread count of the whole inbox and the history per
 * child.
 */
export type InboxPage = {
  items: InboxItem[];
  page: number;
  pageSize: number;
  total: number;
  unread: number;
  children: InboxChild[];
};

/**
 * A registered device as the API returns it (never the token back).
 */
export type DeviceView = {
  id: string;
  platform: RegisterDeviceInput['platform'];
  createdAt: string;
  lastSeenAt: string;
};

/**
 * The channels of one event type for the signed-in person.
 */
export type NotificationPreference = {
  type: NotificationType;
  push: boolean;
  email: boolean;
};
